<script setup lang="ts">
    import { CARD_FOLD_DEFAULT, CARD_FOLD_MAX } from '@/constants/cardFold';
    import TheProduct3DView from '@/components/TheProduct3DView.vue';
    import { useZoomControl } from '@/composables/useZoomControl';
    import { ZOOM_3D_DEFAULT_FACTOR, ZOOM_3D_MIN_FACTOR } from '@/config';
    import { useConfigStore } from '@/store/config.store';
    import { storeToRefs } from 'pinia';
    import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
    import { useI18n } from 'vue-i18n';

    const { foldAngle = CARD_FOLD_DEFAULT, inline = false } = defineProps<{
        foldAngle?: number;
        inline?: boolean;
    }>();

    const { t } = useI18n();

    const { product } = storeToRefs(useConfigStore());
    // Fold toggle only makes sense on a Folded card — a CutShape has
    // a single flat piece.
    const isFoldable = computed(() => product.value?.canFold ?? false);

    const isOpen = ref(false);
    const isFolded = ref(false);
    const isCardAnimated = ref(false);
    const isCardZoomed = ref(false);
    const productViewRef = useTemplateRef<InstanceType<typeof TheProduct3DView> | null>('productViewRef');

    function _getController() {
        return productViewRef.value?.controller ?? null;
    }

    function toggleFold() {
        isFolded.value = !isFolded.value;
        _getController()?.setFold(isFolded.value ? CARD_FOLD_MAX : CARD_FOLD_DEFAULT);
    }

    function toggleCardAnimation() {
        isCardAnimated.value = !isCardAnimated.value;
        _getController()?.camera.setAutoRotate(isCardAnimated.value);
    }

    // The card renders TheProduct3DView, and useEditor3D returns that same cached
    // binding, so the glass and the card drive one camera — they share one zoom
    // policy rather than each keeping their own factor.
    const { canZoom3dIn: canZoomIn, canZoom3dOut: canZoomOut, unZoom: zoomOut, zoom: zoomIn } = useZoomControl();

    function toggleCardZoom() {
        isCardZoomed.value = !isCardZoomed.value;
        // Toggle between the closest framing and the default one. The +/- buttons
        // read the camera back, so they carry on from wherever this leaves it.
        _getController()?.camera.zoomToLevel(isCardZoomed.value ? ZOOM_3D_MIN_FACTOR : ZOOM_3D_DEFAULT_FACTOR);
    }

    // Watch both foldAngle AND the controller ref becoming populated —
    // TheProduct3DView is a template ref child, so on the initial
    // `immediate: true` fire the ref is still null and the setFold
    // call would silently drop. Re-firing once controller.value lands
    // ensures the initial fold reaches the strategy.
    watch(
        [() => foldAngle, () => productViewRef.value?.controller],
        ([val, ctrl]) => {
            if (val !== undefined) {
                ctrl?.setFold(val);
            }
        },
        { immediate: true },
    );

    watch(isOpen, async (val) => {
        if (inline) {
            return;
        }
        if (val) {
            isFolded.value = false;
            isCardAnimated.value = false;
            isCardZoomed.value = false;
            await nextTick();
            _getController()?.setFold(CARD_FOLD_DEFAULT);
        }
    });
</script>

<template>
    <!-- Inline mode: fills parent container -->
    <template v-if="inline">
        <div
            data-test="card-3d-panel-inline"
            class="relative h-full w-full"
        >
            <TheProduct3DView ref="productViewRef" />
            <div class="pointer-events-none absolute bottom-8 right-3 z-10 flex flex-col gap-1.5">
                <button
                    data-test="card-auto-rotate"
                    class="shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition hover:bg-gray-50"
                    @click="toggleCardAnimation"
                >
                    <span class="sr-only">{{ isCardAnimated ? t('pause') : t('play') }}</span>
                    <svg
                        v-if="isCardAnimated"
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-primary h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <rect
                            x="6"
                            y="4"
                            width="4"
                            height="16"
                            rx="1"
                        />
                        <rect
                            x="14"
                            y="4"
                            width="4"
                            height="16"
                            rx="1"
                        />
                    </svg>
                    <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-primary h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path d="M8 5v14l11-7L8 5z" />
                    </svg>
                </button>
                <button
                    class="shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition hover:bg-gray-50"
                    :class="isCardZoomed && 'ring-primary/30 ring-2'"
                    @click="toggleCardZoom"
                >
                    <span class="sr-only">{{ isCardZoomed ? t('unzoom') : t('zoom') }}</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-primary h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <path
                            v-if="isCardZoomed"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9 9L4 4m0 0h5m-5 0v5M15 9l5-5m0 0h-5m5 0v5M9 15l-5 5m0 0h5m-5 0v-5M15 15l5 5m0 0h-5m5 0v-5"
                        />
                        <path
                            v-else
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M4 8V5a1 1 0 011-1h3M4 16v3a1 1 0 001 1h3m8-16h3a1 1 0 011 1v3m0 8v3a1 1 0 01-1 1h-3"
                        />
                    </svg>
                </button>
                <button
                    :class="[
                        canZoomIn && 'hover:bg-gray-50',
                        !canZoomIn && 'cursor-not-allowed opacity-40',
                        'shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition',
                    ]"
                    data-test="card-zoom-in"
                    :disabled="!canZoomIn"
                    @click="zoomIn"
                >
                    <span class="sr-only">{{ t('zoom_in') }}</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-primary h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <circle
                            cx="11"
                            cy="11"
                            r="7"
                        />
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M11 8v6m-3-3h6M21 21l-4.35-4.35"
                        />
                    </svg>
                </button>
                <button
                    :class="[
                        canZoomOut && 'hover:bg-gray-50',
                        !canZoomOut && 'cursor-not-allowed opacity-40',
                        'shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition',
                    ]"
                    data-test="card-zoom-out"
                    :disabled="!canZoomOut"
                    @click="zoomOut"
                >
                    <span class="sr-only">{{ t('zoom_out') }}</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-primary h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <circle
                            cx="11"
                            cy="11"
                            r="7"
                        />
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M8 11h6M21 21l-4.35-4.35"
                        />
                    </svg>
                </button>
            </div>
        </div>
    </template>

    <!-- Panel mode: floating button + slide-in panel -->
    <template v-else>
        <button
            v-if="!isOpen"
            class="shadow-card absolute right-4 top-4 z-20 flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
            @click="isOpen = true"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="text-primary h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
                />
            </svg>
            3D
        </button>

        <Transition
            enter-from-class="translate-x-full opacity-0"
            enter-to-class="translate-x-0 opacity-100"
            leave-from-class="translate-x-0 opacity-100"
            leave-to-class="translate-x-full opacity-0"
            class="duration-300"
        >
            <div
                v-if="isOpen"
                class="absolute right-0 top-0 z-10 flex h-full w-[488px] flex-col border-l border-gray-200 bg-white"
            >
                <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                    <p class="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                        {{ t('preview_label') }}
                    </p>
                    <div class="flex items-center gap-1">
                        <button
                            v-if="isFoldable"
                            class="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition"
                            :class="isFolded ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:bg-gray-100'"
                            @click="toggleFold"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="1.8"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M12 3v18M3 12h18"
                                />
                            </svg>
                            {{ isFolded ? t('unfold') : t('fold') }}
                        </button>
                        <button
                            class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            @click="isOpen = false"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <div
                    data-test="card-3d-panel"
                    class="relative min-h-0 flex-1"
                >
                    <TheProduct3DView ref="productViewRef" />
                    <div class="pointer-events-none absolute bottom-8 right-3 z-10 flex flex-col gap-1.5">
                        <button
                            class="shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition hover:bg-gray-50"
                            @click="toggleCardAnimation"
                        >
                            <span class="sr-only">{{ isCardAnimated ? t('pause') : t('play') }}</span>
                            <svg
                                v-if="isCardAnimated"
                                xmlns="http://www.w3.org/2000/svg"
                                class="text-primary h-4 w-4"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <rect
                                    x="6"
                                    y="4"
                                    width="4"
                                    height="16"
                                    rx="1"
                                />
                                <rect
                                    x="14"
                                    y="4"
                                    width="4"
                                    height="16"
                                    rx="1"
                                />
                            </svg>
                            <svg
                                v-else
                                xmlns="http://www.w3.org/2000/svg"
                                class="text-primary h-4 w-4"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M8 5v14l11-7L8 5z" />
                            </svg>
                        </button>
                        <button
                            class="shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition hover:bg-gray-50"
                            :class="isCardZoomed && 'ring-primary/30 ring-2'"
                            @click="toggleCardZoom"
                        >
                            <span class="sr-only">{{ isCardZoomed ? t('unzoom') : t('zoom') }}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="text-primary h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    v-if="isCardZoomed"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M9 9L4 4m0 0h5m-5 0v5M15 9l5-5m0 0h-5m5 0v5M9 15l-5 5m0 0h5m-5 0v-5M15 15l5 5m0 0h-5m5 0v-5"
                                />
                                <path
                                    v-else
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M4 8V5a1 1 0 011-1h3M4 16v3a1 1 0 001 1h3m8-16h3a1 1 0 011 1v3m0 8v3a1 1 0 01-1 1h-3"
                                />
                            </svg>
                        </button>
                        <button
                            class="shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition hover:bg-gray-50"
                            @click="zoomIn"
                        >
                            <span class="sr-only">{{ t('zoom_in') }}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="text-primary h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <circle
                                    cx="11"
                                    cy="11"
                                    r="7"
                                />
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M11 8v6m-3-3h6M21 21l-4.35-4.35"
                                />
                            </svg>
                        </button>
                        <button
                            class="shadow-card pointer-events-auto flex h-9 w-9 items-center justify-center rounded-xl bg-white transition hover:bg-gray-50"
                            @click="zoomOut"
                        >
                            <span class="sr-only">{{ t('zoom_out') }}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="text-primary h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <circle
                                    cx="11"
                                    cy="11"
                                    r="7"
                                />
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M8 11h6M21 21l-4.35-4.35"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </template>
</template>
